import { CircleConfig } from '@calm/evexia-web-sdk';
import { useIsDarkMode } from '@rhg/hooks/use-provided-theme';
import { ButtonBase } from '@rhg/libs/material';
import NextImage from '@rhg/libs/next/image';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { PARTNER_LOGO_HEIGHT, PARTNER_LOGO_HEIGHT_SMALL } from './types';

interface PartnerImageProps {
    logoSrc: string;
    logoAlt?: string;
    size?: 'small' | 'large';
}
function PartnerImage({ size, logoAlt = '', logoSrc }: PartnerImageProps) {
    return (
        <NextImage
            width={0}
            height={
                size === 'small'
                    ? PARTNER_LOGO_HEIGHT_SMALL
                    : PARTNER_LOGO_HEIGHT
            }
            sizes="100vw"
            style={{
                width: 'auto',
            }}
            data-testid="LOGO"
            alt={logoAlt}
            src={logoSrc}
        />
    );
}

export interface PartnerConfigLogoProps {
    config?: CircleConfig;
    clickable?: boolean;
    size?: 'small' | 'large';
    onClick?: () => void;
}
export const PartnerConfigLogo = ({
    config,
    onClick,
    size = 'large',
    clickable = true,
}: PartnerConfigLogoProps) => {
    const { push } = useRouter();
    const isDarkMode = useIsDarkMode();

    const handleLogoClicked = () => {
        onClick?.();
        push(routes.dashboard);
    };

    const logoAlt = useMemo(() => config?.logo?.altText, [config?.logo]);

    const logoSrc = useMemo(() => {
        if (isDarkMode) {
            return config?.logo?.darkUrl ?? '';
        }
        return config?.logo?.url ?? '';
    }, [isDarkMode, config?.logo?.darkUrl, config?.logo?.url]);

    if (!logoSrc) {
        return null;
    }

    return (
        <span data-testid="partner-logo">
            {clickable ? (
                <ButtonBase
                    onClick={handleLogoClicked}
                    trackButton="PartnerLogo"
                    aria-label={logoAlt}
                    tabIndex={0}
                >
                    <PartnerImage
                        logoAlt={logoAlt}
                        logoSrc={logoSrc}
                        size={size}
                    />
                </ButtonBase>
            ) : (
                <PartnerImage logoAlt={logoAlt} logoSrc={logoSrc} size={size} />
            )}
        </span>
    );
};
